import React, { useEffect, useRef, useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import "./ProgramLifecycleActionsMenu.css";

/**
 * ProgramLifecycleActionsMenu — kebab-menu button that opens the lifecycle
 * actions dropdown for a corp program. Actions are gated on the current
 * `lifecycleState` per Zuri visual spec §1.2 and ADR-049 D1 state machine.
 *
 * Story 1 wired SUSPEND + REACTIVATE with CANCEL disabled ("Coming Sprint
 * 19/20" tooltip); Story 2 (this PR, CORP-PARTNERSHIP-HARD-CANCEL-CORE-001)
 * enables the Cancel item for `active` + `suspended` states. RESTORE
 * remains disabled pending Story 5.
 *
 * Non-applicable actions are NOT rendered (spec §1.2), not just disabled.
 *
 * Props:
 *  - lifecycleState: 'active' | 'suspended' | 'cancelled' | undefined
 *  - onSuspend: () => void
 *  - onReactivate: () => void
 *  - onCancel: () => void          Fires the 4-step hard-cancel wizard
 *  - disabled: boolean              Disable trigger + auto-close menu
 */
const ProgramLifecycleActionsMenu = ({
  lifecycleState,
  onSuspend,
  onReactivate,
  onCancel,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Click-outside + Escape close the menu.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  // Default missing state to `active` so a program row without a
  // lifecycleState field (older BE responses before the migration lands)
  // still shows the Suspend + Cancel(disabled) actions instead of a broken
  // empty menu.
  const state = lifecycleState || "active";

  const handleAction = (fn) => {
    setIsOpen(false);
    if (fn) fn();
  };

  const renderMenuItems = () => {
    if (state === "active") {
      return (
        <>
          <button
            type="button"
            role="menuitem"
            className="program-lifecycle-menu-item is-suspend"
            onClick={() => handleAction(onSuspend)}
          >
            Suspend
          </button>
          <button
            type="button"
            role="menuitem"
            className="program-lifecycle-menu-item is-cancel"
            onClick={() => handleAction(onCancel)}
          >
            Cancel
          </button>
        </>
      );
    }
    if (state === "suspended") {
      return (
        <>
          <button
            type="button"
            role="menuitem"
            className="program-lifecycle-menu-item is-reactivate"
            onClick={() => handleAction(onReactivate)}
          >
            Reactivate
          </button>
          <button
            type="button"
            role="menuitem"
            className="program-lifecycle-menu-item is-cancel"
            onClick={() => handleAction(onCancel)}
          >
            Cancel
          </button>
        </>
      );
    }
    if (state === "cancelled") {
      return (
        <button
          type="button"
          role="menuitem"
          className="program-lifecycle-menu-item is-restore"
          disabled
          aria-disabled="true"
          title="Coming in Sprint 20 (Story 5 RESTORE)"
        >
          Restore
        </button>
      );
    }
    return null;
  };

  return (
    <div
      className="program-lifecycle-menu-container"
      ref={containerRef}
    >
      <button
        type="button"
        ref={triggerRef}
        className="program-lifecycle-menu-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Program lifecycle actions"
        data-testid="program-lifecycle-menu-trigger"
      >
        <FaEllipsisV aria-hidden="true" />
      </button>
      {isOpen ? (
        <div
          className="program-lifecycle-menu-dropdown"
          role="menu"
          aria-label="Program lifecycle actions"
        >
          {renderMenuItems()}
        </div>
      ) : null}
    </div>
  );
};

export default ProgramLifecycleActionsMenu;
